import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { ArticleJsonLd } from '@/components/feature/SEOJsonLd';

const categoryNames: Record<string, string> = {
  'zuixin-dizhi': '最新地址',
  'xianlu-gengxin': '线路更新',
  'shiyong-zhinan': '使用指南',
  'changjian-wenti': '常见问题',
  'anquan-fanghu': '安全防护',
};

const articleHeroImages: Record<string, string> = {
  'aiqingdao-zuixin-rukou': 'https://readdy.ai/api/search-image?query=Elegant%20warm%20abstract%20composition%20with%20soft%20rose%20and%20golden%20gradients%2C%20flowing%20organic%20shapes%2C%20minimalist%20aesthetic%2C%20editorial%20quality%2C%20no%20text%2C%20clean%20design&width=1600&height=500&seq=detail-addr-01&orientation=landscape',
  'dizhi-biangeng-tongzhi': 'https://readdy.ai/api/search-image?query=Warm%20coral%20and%20amber%20abstract%20background%20with%20gentle%20curves%2C%20minimalist%20elegant%20design%2C%20editorial%20style%2C%20no%20text%2C%20soft%20diffused%20light&width=1600&height=500&seq=detail-addr-02&orientation=landscape',
  'shouci-fangwen-jiaocheng': 'https://readdy.ai/api/search-image?query=Soft%20peach%20and%20cream%20abstract%20gradient%2C%20subtle%20organic%20shapes%2C%20minimalist%20clean%20aesthetic%2C%20editorial%20quality%2C%20no%20text%2C%20calm%20atmosphere&width=1600&height=500&seq=detail-guide-01&orientation=landscape',
  'yidong-duan-fangwen': 'https://readdy.ai/api/search-image?query=Gentle%20rosy%20pink%20and%20warm%20beige%20abstract%2C%20soft%20flowing%20gradients%2C%20minimalist%20design%2C%20editorial%20aesthetic%2C%20no%20text%2C%20peaceful%20mood&width=1600&height=500&seq=detail-mobile-01&orientation=landscape',
  'wu-fa-dakai-jiejue': 'https://readdy.ai/api/search-image?query=Warm%20golden%20and%20blush%20abstract%20gradient%2C%20soft%20organic%20lines%2C%20minimalist%20elegant%2C%20editorial%20style%2C%20no%20text%2C%20harmonious%20composition&width=1600&height=500&seq=detail-fix-01&orientation=landscape',
  'anquan-fanghu-jianyi': 'https://readdy.ai/api/search-image?query=Subtle%20terracotta%20and%20cream%20abstract%20background%2C%20gentle%20shapes%2C%20minimalist%20aesthetic%2C%20editorial%20quality%2C%20no%20text%2C%20soft%20lighting&width=1600&height=500&seq=detail-safety-01&orientation=landscape',
  'luntan-gongneng-jieshao': 'https://readdy.ai/api/search-image?query=Delicate%20rose%20and%20golden%20honey%20abstract%2C%20soft%20curves%2C%20minimal%20elegant%20design%2C%20editorial%20quality%2C%20no%20text%2C%20peaceful%20vibe&width=1600&height=500&seq=detail-features-01&orientation=landscape',
  'zhuce-zhanghao-jiaocheng': 'https://readdy.ai/api/search-image?query=Warm%20apricot%20and%20soft%20pink%20abstract%2C%20gentle%20flowing%20shapes%2C%20minimalist%20aesthetic%2C%20editorial%20quality%2C%20no%20text%2C%20harmonious%20tones&width=1600&height=500&seq=detail-register-01&orientation=landscape',
  'dizhi-shouji-changjian': 'https://readdy.ai/api/search-image?query=Soft%20coral%20and%20warm%20sand%20abstract%2C%20subtle%20organic%20textures%2C%20minimalist%20design%2C%20editorial%20aesthetic%2C%20no%20text%2C%20calm%20atmosphere&width=1600&height=500&seq=detail-bookmark-01&orientation=landscape',
  'banben-gengxin-jilu': 'https://readdy.ai/api/search-image?query=Elegant%20amber%20and%20blush%20abstract%2C%20soft%20diffused%20gradients%2C%20minimal%20clean%20design%2C%20editorial%20quality%2C%20no%20text%2C%20gentle%20lighting&width=1600&height=500&seq=detail-changelog-01&orientation=landscape',
  'lianxi-wo-men': 'https://readdy.ai/api/search-image?query=Warm%20peach%20and%20cream%20abstract%20with%20subtle%20shapes%2C%20minimalist%20elegant%2C%20editorial%20quality%2C%20no%20text%2C%20peaceful%20calm%20mood&width=1600&height=500&seq=detail-contact-01&orientation=landscape',
  'xin-shou-bi-du': 'https://readdy.ai/api/search-image?query=Soft%20rose%20gold%20and%20warm%20beige%20abstract%2C%20gentle%20organic%20flows%2C%20minimalist%20aesthetic%2C%20editorial%20quality%2C%20no%20text%2C%20harmonious%20composition&width=1600&height=500&seq=detail-beginner-01&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const heroImage = slug ? (articleHeroImages[slug] || articleHeroImages['aiqingdao-zuixin-rukou']) : '';

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-file-unknow-line text-foreground-400 text-2xl"></i>
          </div>
          <p className="text-foreground-600 text-sm mb-4">文章未找到</p>
          <Link
            to="/"
            className="text-primary-500 hover:text-primary-600 text-sm font-medium cursor-pointer"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const categoryName = categoryNames[article.category] || article.category;

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  return (
    <>
      <ArticleJsonLd
        title={article.title}
        description={article.excerpt}
        url={`/article/${article.slug}`}
        datePublished={article.publishedAt}
        dateModified={article.publishedAt}
      />

      <article>
        <div className="relative h-[280px] md:h-[380px] overflow-hidden">
          <img
            src={heroImage}
            alt={article.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/40"></div>
          <div className="absolute bottom-0 left-0 right-0">
            <div className="max-w-4xl mx-auto px-4 md:px-6 pb-8">
              <div className="flex items-center gap-2 mb-3">
                <Link
                  to={`/category/${article.category}`}
                  className="text-xs bg-primary-500/80 text-white px-3 py-1 rounded-full whitespace-nowrap cursor-pointer"
                >
                  {categoryName}
                </Link>
                <span className="text-xs text-white/60 whitespace-nowrap">
                  {article.readTime}
                </span>
                <span className="text-xs text-white/60 whitespace-nowrap">
                  {article.publishedAt}
                </span>
              </div>
              <h1 className="font-heading text-xl md:text-2xl font-bold text-white leading-snug">
                {article.title}
              </h1>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryName, href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <div
            className="prose-custom py-8"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="border-t border-background-200/70 pt-8 pb-12">
            <h3 className="font-heading text-lg font-semibold text-foreground-950 mb-4">
              相关文章
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.slug}
                  to={`/article/${rel.slug}`}
                  className="bg-background-100 rounded-lg p-4 hover:bg-background-200/70 transition-colors cursor-pointer group"
                >
                  <h4 className="font-heading text-sm font-medium text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {rel.title}
                  </h4>
                  <p className="text-xs text-foreground-500 line-clamp-2 mb-2">
                    {rel.excerpt}
                  </p>
                  <span className="text-xs text-foreground-400">
                    {rel.publishedAt}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>
    </>
  );
}